import { useCallback, useMemo, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { EditorView, keymap } from '@codemirror/view'
import { Compartment } from '@codemirror/state'
import {
  defaultKeymap,
  historyKeymap,
  history,
  toggleComment,
  indentWithTab,
} from '@codemirror/commands'
import {
  search,
  searchKeymap,
  openSearchPanel,
  gotoLine,
} from '@codemirror/search'
import './CodeMirrorWithWrap.css'

/**
 * CodeMirror wrapper — 종합 도구 panel 내장.
 *
 * 우상단 floating toolbar:
 *   🔍 찾기/바꾸기  (Ctrl+F)  — CodeMirror 검색 panel (replace 포함)
 *   ⤓ 라인 점프    (Ctrl+G)  — gotoLine
 *   /  주석 토글    (Ctrl+/)  — toggleComment
 *   A− / A+ 폰트     — per-instance 폰트 크기 (Compartment 로 hot reconfig)
 *   ↩ 워드랩         — per-instance 줄바꿈 (Compartment 로 hot reconfig)
 *
 * 하단 status bar (showPanel API):
 *   Line N, Col M  •  N 줄 / M 자  •  선택: K 자 (선택 시)
 *
 * 모든 도구 상태는 인스턴스별. extensions 는 한 번만 구성 (Compartment 로
 * dynamic reconfig) → CodeMirror re-init 방지.
 */

const FONT_MIN = 9
const FONT_MAX = 28
const FONT_DEFAULT = 13

// 부모 컴포넌트 CSS 가 `.parent .cm-editor .cm-content { font-size }` 형태로
// 더 높은 specificity (0,3,0) 로 font-size 를 박아두는 경우가 많아, theme 의
// 0,2,0 selector 로는 못 이긴다 → !important 로 강제 override.
function makeFontTheme(px) {
  const sz = `${px}px !important`
  return EditorView.theme({
    '&':            { fontSize: sz },
    '.cm-content':  { fontSize: sz },
    '.cm-gutters':  { fontSize: sz },
    '.cm-line':     { fontSize: sz },
  })
}

export default function CodeMirrorWithWrap({
  extensions,
  // KBNL-136(feedback 20260618): 워드랩을 기본 ON. 가로 스크롤로 잘려 보이던
  // 긴 SQL/YAML 라인이 기본적으로 줄바꿈되어 한눈에 보임. 툴바 ↩ 로 끌 수 있음.
  wrapDefault = true,
  fontSizeDefault = FONT_DEFAULT,
  // KBNL-136(feedback 20260618): 주어지면 툴바에 '이 줄 기준 맞춤(⤒)' 버튼 노출.
  // 클릭 시 onAlignPeer(thisView) 호출 → 호출측이 반대쪽 편집기를 이 줄에 1회 정렬.
  onAlignPeer,
  ...cmProps
}) {
  const [wrap, setWrap] = useState(!!wrapDefault)
  const [fontSize, setFontSize] = useState(fontSizeDefault)
  const [stats, setStats] = useState({ line: 1, col: 1, lines: 1, chars: 0, selection: 0 })
  const viewRef = useRef(null)
  // KBNL-136(feedback 20260618): 떠 있는 툴바가 코드를 가릴 때 마우스로 이동.
  // 드래그하면 화면 기준 position:fixed 로 띄워 편집기 overflow:hidden 에 잘리거나
  // 다른 패널 밑으로 숨지 않음 (+ 높은 z-index). null = 기본 위치(우상단 sticky),
  // {left,top} = 드래그된 고정 위치. grip 더블클릭하면 기본 위치로 복귀.
  const [toolPos, setToolPos] = useState(null)
  const onGripDown = useCallback((e) => {
    e.preventDefault()
    const bar = e.currentTarget.parentElement   // .cm-tool-bar
    const rect = bar.getBoundingClientRect()
    const offX = e.clientX - rect.left
    const offY = e.clientY - rect.top
    const onMove = (ev) => {
      setToolPos({ left: ev.clientX - offX, top: ev.clientY - offY })
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }, [])

  // Compartment — 한 번 만들고 reconfigure dispatch 로 hot swap.
  // KBNL-141: useRef(...).current 는 render 중 ref 접근(react-hooks/refs)에 걸림 →
  // useMemo 로 생성(컴포넌트 수명 동안 안정 인스턴스, Offscreen 미사용 환경).
  const wrapCompartment = useMemo(() => new Compartment(), [])
  const fontCompartment = useMemo(() => new Compartment(), [])

  // 통계 업데이트 — setState 는 stable 이라 한 번만 만들어도 됨
  const updateListener = useMemo(() =>
    EditorView.updateListener.of((update) => {
      if (!update.docChanged && !update.selectionSet) return
      const view = update.view
      const sel = view.state.selection.main
      const line = view.state.doc.lineAt(sel.head)
      setStats({
        line: line.number,
        col: sel.head - line.from + 1,
        lines: view.state.doc.lines,
        chars: view.state.doc.length,
        selection: sel.empty ? 0 : sel.to - sel.from,
      })
    }),
    []
  )

  // 최종 extensions — parent 의 extensions + 도구. wrap/font 는 Compartment 안.
  const finalExtensions = useMemo(() => {
    const base = extensions || []
    return [
      ...base,
      history(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...searchKeymap,
        indentWithTab,
      ]),
      search({ top: true }),
      wrapCompartment.of(wrapDefault ? EditorView.lineWrapping : []),
      fontCompartment.of(makeFontTheme(fontSizeDefault)),
      updateListener,
    ]
    // extensions 만 deps — wrap/font 는 dispatch 로 swap, deps 에서 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extensions])

  const handleCreateEditor = useCallback((view) => {
    viewRef.current = view
    if (typeof cmProps.onCreateEditor === 'function') cmProps.onCreateEditor(view)
  }, [cmProps.onCreateEditor])

  // ── 도구 액션 ──────────────────────────────────────────────────────────
  const openSearch = useCallback(() => {
    const view = viewRef.current
    if (!view) return
    view.focus()
    openSearchPanel(view)
  }, [])

  const callGotoLine = useCallback(() => {
    const view = viewRef.current
    if (!view) return
    // KBNL-136(feedback 20260618): 라인 점프 패널 중복 방지.
    // gotoLine 패널은 하단(.cm-panels-bottom)에 뜨는데, 버튼을 여러 번 누르면
    // 패널/포커스가 꼬여 여러 개가 보였다 사라지는 현상이 있었음. 이미 열린
    // 입력이 있으면 새로 열지 않고 그 입력에 포커스만 준다. (view.focus() 도
    // 제거 — gotoLine 이 자체적으로 입력에 포커스하므로 포커스 thrash 방지)
    const existing = view.dom.querySelector('.cm-panels-bottom input')
    if (existing) {
      existing.focus()
      if (typeof existing.select === 'function') existing.select()
      return
    }
    gotoLine(view)
  }, [])

  const callToggleComment = useCallback(() => {
    const view = viewRef.current
    if (!view) return
    view.focus()
    toggleComment(view)
  }, [])

  const toggleWrap = useCallback(() => {
    const view = viewRef.current
    const newVal = !wrap
    setWrap(newVal)
    if (view) {
      view.dispatch({
        effects: wrapCompartment.reconfigure(newVal ? EditorView.lineWrapping : []),
      })
    }
  }, [wrap, wrapCompartment])

  const changeFontSize = useCallback((delta) => {
    const view = viewRef.current
    const next = Math.max(FONT_MIN, Math.min(FONT_MAX, fontSize + delta))
    if (next === fontSize) return
    setFontSize(next)
    if (view) {
      view.dispatch({
        effects: fontCompartment.reconfigure(makeFontTheme(next)),
      })
    }
  }, [fontSize, fontCompartment])

  return (
    <div className="cm-with-wrap">
      <CodeMirror
        {...cmProps}
        extensions={finalExtensions}
        onCreateEditor={handleCreateEditor}
      />

      {/* 우상단 도구 바 — hover 시 진해짐. grip 드래그로 위치 이동 (코드 가림 회피).
          드래그 시 position:fixed + 높은 z-index 로 어떤 패널에도 안 가리고 안 잘림. */}
      <div
        className="cm-tool-bar"
        style={toolPos ? {
          position: 'fixed', left: toolPos.left, top: toolPos.top,
          right: 'auto', margin: 0, zIndex: 3000, opacity: 1,
        } : undefined}
      >
        <span
          className="cm-tool-grip"
          onMouseDown={onGripDown}
          onDoubleClick={() => setToolPos(null)}
          title="드래그하여 툴바 이동 · 더블클릭하면 원위치"
          aria-label="툴바 이동 핸들"
        >⠿</span>
        <button
          type="button"
          className="cm-tool-btn"
          onClick={openSearch}
          title="찾기/바꾸기 (Ctrl+F)"
          aria-label="찾기"
        >🔍</button>
        <button
          type="button"
          className="cm-tool-btn"
          onClick={callGotoLine}
          title="라인 점프 (Ctrl+G)"
          aria-label="라인 점프"
        >⤓</button>
        <button
          type="button"
          className="cm-tool-btn"
          onClick={callToggleComment}
          title="주석 토글 (Ctrl+/)"
          aria-label="주석 토글"
        ><span className="cm-tool-comment">/⁄</span></button>
        {onAlignPeer && (
          <button
            type="button"
            className="cm-tool-btn"
            onClick={() => onAlignPeer(viewRef.current)}
            title="이 줄 기준 맞춤 — 반대쪽 편집기를 현재 첫 줄에 맞춤 (1회)"
            aria-label="이 줄 기준 맞춤"
          >⤒</button>
        )}
        <span className="cm-tool-sep" />
        <button
          type="button"
          className="cm-tool-btn"
          onClick={() => changeFontSize(-1)}
          disabled={fontSize <= FONT_MIN}
          title={`폰트 작게 (현재 ${fontSize}px)`}
          aria-label="폰트 작게"
        >A−</button>
        <span className="cm-tool-font-size" title="현재 폰트 크기">{fontSize}</span>
        <button
          type="button"
          className="cm-tool-btn"
          onClick={() => changeFontSize(+1)}
          disabled={fontSize >= FONT_MAX}
          title={`폰트 크게 (현재 ${fontSize}px)`}
          aria-label="폰트 크게"
        >A+</button>
        <span className="cm-tool-sep" />
        <button
          type="button"
          className={`cm-tool-btn ${wrap ? 'is-on' : ''}`}
          onClick={toggleWrap}
          title={wrap ? '워드랩 끄기' : '워드랩 켜기'}
          aria-label={wrap ? '워드랩 끄기' : '워드랩 켜기'}
        >↩</button>
      </div>

      {/* 하단 status bar — 통계 */}
      <div className="cm-status-bar" title="현재 커서 위치 / 전체 / 선택">
        <span className="cm-stat">
          <span className="cm-stat-key">Ln</span> {stats.line}
          <span className="cm-stat-key">Col</span> {stats.col}
        </span>
        <span className="cm-stat-sep">·</span>
        <span className="cm-stat">
          {stats.lines} 줄 · {stats.chars} 자
        </span>
        {stats.selection > 0 && (
          <>
            <span className="cm-stat-sep">·</span>
            <span className="cm-stat cm-stat-sel">선택 {stats.selection} 자</span>
          </>
        )}
      </div>
    </div>
  )
}
